/**
 * People & access — mockup 5B.
 *
 * Extracted from Settings.jsx, which was 1,230 lines with four unrelated admin
 * surfaces in it.
 *
 * What the mockup changes, in order of how much it matters:
 *
 * 1. **Exclusions were write-only.** Excluding someone dropped them at the Graph
 *    fetch filter, so they vanished from this table too — there was no list of who
 *    was excluded and no way to undo it from the UI. The Excluded tab now shows the
 *    stored rules and removes them one at a time. It has to work rule by rule
 *    rather than person by person: an excluded person is not in the directory to
 *    look up, and the id and name rule lists cannot be joined back together.
 *
 * 2. **Exclude hid inside the role dropdown** and fired on click with no
 *    confirmation. Role and exclusion are now separate controls, and exclusion is
 *    explicitly destructive with a typed confirmation.
 *
 * 3. **The table was a `<table>` with its own densities.** It is now the same 52px
 *    row grid as the Directory, so scanning people reads the same way everywhere.
 *
 * 4. **Corrections were invisible until you opened the editor.** A row now says how
 *    many fields are overridden, and the Corrected tab lists just those people.
 *
 * The one thing from the mockup not done here: it moves Exclude into the danger
 * zone with its own person picker, and routes Correct through the same panel the
 * org chart uses. Both are shared-component work; the row-level controls below are
 * the same behaviour without the sharing.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { getAllRoles, setUserRole } from '../../services/userRolesService';
import {
  excludeUser,
  getExclusionRules,
  removeExclusionRule,
} from '../../services/excludedUsersService';
import { getCorrections, setCorrection } from '../../services/correctionsService';
import { useAuth } from '../../hooks/useAuth';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { PortalSelect } from '../ui/PortalSelect';
import { deptScheme } from '../../utils/deptColors';
import { correctedFieldCount } from '../../utils/corrections';

/** Same shape as the Directory's rows: floors on the flexible tracks so a long
 *  title truncates at the ceiling instead of collapsing the column to zero. */
const ROW_COLS =
  'grid items-center gap-3 grid-cols-[minmax(0,1fr)_112px] ' +
  'md:grid-cols-[minmax(200px,2.2fr)_minmax(140px,1.4fr)_112px_128px] ' +
  'lg:grid-cols-[minmax(210px,2.2fr)_minmax(150px,1.4fr)_112px_104px_128px]';

const TABS = [
  { key: 'all', label: 'All' },
  { key: 'admins', label: 'Admins' },
  { key: 'corrected', label: 'Corrected' },
  { key: 'excluded', label: 'Excluded' },
];

/** Name rules are stored normalized (lowercased, collapsed spaces). */
function titleCase(name) {
  return (name || '').replace(/\b[a-z]/g, (c) => c.toUpperCase());
}

function Chip({ on, children, onClick, count }) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full border text-[12px] font-semibold transition-colors
        ${on
          ? 'bg-accent-soft border-accent-line text-accent'
          : 'bg-surface border-line text-ink-2 hover:bg-surface-hover'}`}
    >
      {children}
      {count != null && (
        <span className={`font-mono text-[11px] font-semibold ${on ? 'text-accent' : 'text-ink-3'}`}>
          {count}
        </span>
      )}
    </button>
  );
}

function Pill({ tone = 'neutral', children, title }) {
  const tones = {
    ok: 'bg-accent-soft border-accent-line text-accent',
    neutral: 'bg-surface-2 border-line-strong text-ink-3',
    off: 'bg-surface-2 border-line-strong text-ink-3 line-through decoration-1',
  };
  return (
    <span
      title={title}
      className={`inline-flex items-center gap-1 px-2 h-[22px] rounded-full border text-[11px] font-bold whitespace-nowrap ${tones[tone]}`}
    >
      {children}
    </span>
  );
}

function Avatar({ user, photo }) {
  if (photo) {
    return <img src={photo} alt="" className="w-7 h-7 rounded-full object-cover object-top flex-none" />;
  }
  const initials = `${user.givenName?.[0] ?? user.displayName?.[0] ?? '?'}${user.surname?.[0] ?? ''}`;
  return (
    <span
      className="w-7 h-7 rounded-full flex-none inline-flex items-center justify-center text-[10.5px] font-bold text-white"
      style={{ background: deptScheme(user.department).accent }}
      aria-hidden
    >
      {initials}
    </span>
  );
}

export function PeopleAccess({ employees, photos, currentUserId, onDirectoryRefresh, reviewFilter = null }) {
  const { getApiToken } = useAuth();
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('all');
  const [saving, setSaving] = useState(null);
  const [corrections, setCorrections] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [pendingExclude, setPendingExclude] = useState(null);
  const [rules, setRules] = useState({ ids: [], names: [] });
  const [rulesDirty, setRulesDirty] = useState(false);
  const [editDept, setEditDept] = useState('');
  const [editManagerId, setEditManagerId] = useState('');
  const [editJobTitle, setEditJobTitle] = useState('');
  const [editTopOfOrg, setEditTopOfOrg] = useState(false);
  const [editManagerQuery, setEditManagerQuery] = useState('');
  const [corrSaving, setCorrSaving] = useState(false);
  const [corrError, setCorrError] = useState(null);

  useEffect(() => { setUsers(getAllRoles(employees)); }, [employees]);
  useEffect(() => { setRules(getExclusionRules()); }, []);
  useEffect(() => {
    getApiToken().then((token) => getCorrections(token)).then(setCorrections).catch(() => {});
  }, [getApiToken]);
  useEffect(() => {
    if (!reviewFilter) return;
    setTab('all');
    setSearch('');
    setEditingId(null);
    setPendingExclude(null);
  }, [reviewFilter]);

  function openEdit(user) {
    const c = corrections[user.id] ?? {};
    setEditingId(user.id);
    setEditDept(c.department ?? user.department ?? '');
    setEditManagerId(c.managerId ?? user.managerId ?? '');
    setEditJobTitle(c.jobTitle ?? user.jobTitle ?? '');
    setEditTopOfOrg(Boolean(c.topOfOrg ?? user.topOfOrg));
    setEditManagerQuery('');
    setCorrError(null);
  }

  function closeEdit() {
    setEditingId(null);
    setCorrError(null);
  }

  async function saveCorrection(userId) {
    setCorrSaving(true);
    setCorrError(null);
    try {
      const token = await getApiToken();
      const patch = {
        department: editDept.trim() || null,
        jobTitle: editJobTitle.trim() || null,
      };
      if (editTopOfOrg) {
        patch.topOfOrg = true;
        patch.managerId = null;
      } else {
        patch.managerId = editManagerId || null;
        patch.topOfOrg = null;
      }
      await setCorrection(token, userId, patch);
      setCorrections((prev) => ({ ...prev, [userId]: { ...(prev[userId] ?? {}), ...patch } }));
      closeEdit();
    } catch (err) {
      setCorrError('Failed to save. Try again.');
      console.error('Correction save failed:', err);
    } finally {
      setCorrSaving(false);
    }
  }

  const handleRoleChange = (employeeId, newRole) => {
    if (employeeId === currentUserId) return;
    setSaving(employeeId);
    setUserRole(employeeId, newRole);
    setUsers((prev) => prev.map((u) => (u.id === employeeId ? { ...u, role: newRole } : u)));
    setTimeout(() => setSaving(null), 600);
  };

  const confirmExclude = () => {
    const user = pendingExclude;
    if (!user) return;
    // The name goes in too — it is what keeps a re-synced account (same person,
    // new object id) from reappearing on the next sync.
    excludeUser(user.id, user.displayName);
    setUsers((prev) => prev.filter((u) => u.id !== user.id));
    setRules(getExclusionRules());
    setPendingExclude(null);
  };

  const removeRule = (rule) => {
    if (removeExclusionRule(rule)) {
      setRules(getExclusionRules());
      setRulesDirty(true);
    }
  };

  const adminCount = users.filter((u) => u.role === 'admin').length;
  const correctedCount = users.filter((u) => correctedFieldCount(corrections[u.id]) > 0).length;
  const ruleCount = rules.ids.length + rules.names.length;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users
      .filter((u) => {
        if (tab === 'admins' && u.role !== 'admin') return false;
        if (tab === 'corrected' && correctedFieldCount(corrections[u.id]) === 0) return false;
        const resolvedDepartment = corrections[u.id]?.department ?? u.department;
        if (reviewFilter === 'missingDepartment' && resolvedDepartment) return false;
        if (!q) return true;
        return (
          (u.displayName ?? '').toLowerCase().includes(q) ||
          (u.mail ?? '').toLowerCase().includes(q) ||
          (resolvedDepartment ?? '').toLowerCase().includes(q) ||
          ((corrections[u.id]?.jobTitle ?? u.jobTitle) ?? '').toLowerCase().includes(q)
        );
      })
      .sort((a, b) => (a.displayName ?? '').localeCompare(b.displayName ?? ''));
  }, [users, tab, search, corrections, reviewFilter]);

  const managerCandidates = editManagerQuery.trim()
    ? employees
        .filter((e) => e.id !== editingId && e.displayName?.toLowerCase().includes(editManagerQuery.toLowerCase()))
        .slice(0, 6)
    : [];

  return (
    <div className="space-y-3.5">
      <div>
        <h3 className="text-[16px] font-bold text-ink mb-[3px]">People &amp; access</h3>
        <p className="text-[12.5px] text-ink-3 leading-[1.5]">
          <span className="font-mono">{users.length}</span> people ·{' '}
          <span className="font-mono">{adminCount}</span> admin{adminCount === 1 ? '' : 's'} ·{' '}
          <span className="font-mono">{correctedCount}</span> with corrections ·{' '}
          <span className="font-mono">{ruleCount}</span> exclusion rule{ruleCount === 1 ? '' : 's'}.
          Role changes affect access for everyone.
        </p>
      </div>

      {reviewFilter === 'missingDepartment' && (
        <div className="rounded-card border border-amber-300/70 bg-amber-50/70 px-3 py-2.5 text-[12.5px] text-ink-2">
          Reviewing only people who still have no department after corrections. Use <span className="font-semibold">Correct…</span> to file them.
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative w-full sm:w-[280px]">
          <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-ink-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" />
          </svg>
          <input
            type="search"
            placeholder="Search name, email, department, title…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full h-8 pl-8 pr-3 text-[12.5px] rounded-ctl bg-surface border border-line text-ink
                       placeholder:text-ink-3 focus:outline-none focus:border-accent"
          />
        </div>
        {TABS.map(({ key, label }) => (
          <Chip
            key={key}
            on={tab === key}
            onClick={() => setTab(key)}
            count={
              key === 'all' ? users.length
                : key === 'admins' ? adminCount
                  : key === 'corrected' ? correctedCount
                    : ruleCount
            }
          >
            {label}
          </Chip>
        ))}
      </div>

      {tab === 'excluded' ? (
        <ExclusionRules
          rules={rules}
          onRemove={removeRule}
          dirty={rulesDirty}
          onRefresh={onDirectoryRefresh}
        />
      ) : (
        <div className="rounded-card border border-line bg-surface shadow-e1 overflow-hidden">
          {/* .thead — a transparent border keeps the header's box model identical to
              a row's, so the labels line up with the cells beneath them. */}
          <div className={`${ROW_COLS} h-9 px-3.5 bg-surface-2 border-b border-line
                           text-[10.5px] font-bold uppercase tracking-[0.07em] text-ink-3`}>
            <div>Employee</div>
            <div className="hidden md:block">Department &amp; title</div>
            <div>Role</div>
            <div className="hidden lg:block">Corrections</div>
            <div className="hidden md:block text-right">Actions</div>
          </div>

          <div>
            {filtered.map((user) => {
              const isSelf = user.id === currentUserId;
              const isEditing = editingId === user.id;
              const correction = corrections[user.id];
              const corrected = correctedFieldCount(correction);
              const dept = correction?.department ?? user.department;
              const title = correction?.jobTitle ?? user.jobTitle;
              const scheme = deptScheme(dept);
              return (
                <React.Fragment key={user.id}>
                  <div className={`${ROW_COLS} min-h-[52px] px-3.5 border-b border-line hover:bg-surface-hover transition-colors`}>
                    <div className="flex items-center gap-2.5 min-w-0">
                      <Avatar user={user} photo={photos?.[user.id]} />
                      <span className="min-w-0">
                        <span className="block text-[13px] font-semibold text-ink truncate">
                          {user.displayName}
                          {isSelf && <span className="text-[11px] font-normal text-ink-3"> (you)</span>}
                        </span>
                        <span className="block text-[11px] text-ink-3 truncate">{user.mail}</span>
                      </span>
                    </div>

                    <div className="hidden md:block min-w-0">
                      <span className="flex items-center gap-1.5 text-[12px] text-ink-2 truncate">
                        <span className="w-1.5 h-1.5 rounded-full flex-none" style={{ background: scheme.accent }} aria-hidden />
                        <span className="truncate">{dept || 'Unassigned'}</span>
                      </span>
                      <span className="block text-[11px] text-ink-3 truncate">{title}</span>
                    </div>

                    <div>
                      {isSelf ? (
                        <Pill tone="ok" title="You cannot change your own role — that is what stops an admin locking themselves out">
                          Admin
                        </Pill>
                      ) : (
                        <PortalSelect
                          value={user.role}
                          disabled={saving === user.id}
                          onChange={(role) => handleRoleChange(user.id, role)}
                          ariaLabel={`Role for ${user.displayName}`}
                          className="w-full max-w-[110px]"
                          options={[
                            { value: 'user', label: 'User' },
                            { value: 'admin', label: 'Admin' },
                          ]}
                        />
                      )}
                    </div>

                    <div className="hidden lg:block">
                      {corrected > 0 ? (
                        <Pill tone="ok" title={`Overrides Vantagepoint and Azure AD for ${corrected} field${corrected === 1 ? '' : 's'}`}>
                          {corrected} field{corrected === 1 ? '' : 's'}
                        </Pill>
                      ) : (
                        <span className="text-[12px] text-ink-3">—</span>
                      )}
                    </div>

                    <div className="hidden md:flex items-center justify-end gap-1.5">
                      <button
                        onClick={() => (isEditing ? closeEdit() : openEdit(user))}
                        className={`h-7 px-2.5 text-[12px] font-semibold rounded-ctl border transition-colors
                          ${isEditing
                            ? 'border-accent-line bg-accent-soft text-accent'
                            : 'border-line-strong bg-surface text-ink-2 hover:bg-surface-hover'}`}
                      >
                        {isEditing ? 'Cancel' : 'Correct…'}
                      </button>
                      {isSelf ? (
                        <span className="text-[11.5px] text-ink-3">Cannot edit self</span>
                      ) : (
                        <button
                          onClick={() => setPendingExclude(user)}
                          title="Remove this person from the directory for everyone"
                          className="h-7 px-2.5 text-[12px] font-semibold rounded-ctl border transition-colors
                                     border-red-300/70 dark:border-red-900/70 text-red-700 dark:text-red-400
                                     hover:bg-red-50 dark:hover:bg-red-950/30"
                        >
                          Exclude…
                        </button>
                      )}
                    </div>
                  </div>

                  {isEditing && (
                    <div className="px-3.5 py-3.5 border-b border-line bg-accent-soft/40">
                      <div className="flex flex-wrap gap-3 items-end">
                        <label className="flex-1 min-w-[180px]">
                          <span className="block text-[11px] font-bold uppercase tracking-[0.06em] text-ink-3 mb-1">Department</span>
                          <input
                            type="text"
                            value={editDept}
                            onChange={(e) => setEditDept(e.target.value)}
                            placeholder={user.department ?? 'Enter department…'}
                            className="w-full h-8 px-2.5 text-[12.5px] rounded-ctl bg-surface border border-line text-ink focus:outline-none focus:border-accent"
                          />
                        </label>
                        <label className="flex-1 min-w-[180px]">
                          <span className="block text-[11px] font-bold uppercase tracking-[0.06em] text-ink-3 mb-1">Title</span>
                          <input
                            type="text"
                            value={editJobTitle}
                            onChange={(e) => setEditJobTitle(e.target.value)}
                            placeholder={user.jobTitle ?? 'Enter title…'}
                            className="w-full h-8 px-2.5 text-[12.5px] rounded-ctl bg-surface border border-line text-ink focus:outline-none focus:border-accent"
                          />
                        </label>
                        <div className="flex-1 min-w-[180px] relative">
                          <span className="block text-[11px] font-bold uppercase tracking-[0.06em] text-ink-3 mb-1">Reports to</span>
                          <input
                            type="text"
                            value={editManagerQuery}
                            onChange={(e) => {
                              setEditManagerQuery(e.target.value);
                              if (!e.target.value) setEditManagerId('');
                            }}
                            disabled={editTopOfOrg}
                            placeholder={
                              editManagerId
                                ? (employees.find((e) => e.id === editManagerId)?.displayName ?? 'Selected')
                                : 'Search by name…'
                            }
                            className="w-full h-8 px-2.5 text-[12.5px] rounded-ctl bg-surface border border-line text-ink
                                       focus:outline-none focus:border-accent disabled:opacity-55"
                          />
                          {managerCandidates.length > 0 && (
                            <div className="absolute top-full left-0 right-0 z-10 mt-1 rounded-card bg-surface border border-line shadow-e3 overflow-hidden">
                              {managerCandidates.map((m) => (
                                <button
                                  key={m.id}
                                  onClick={() => { setEditManagerId(m.id); setEditManagerQuery(''); }}
                                  className="w-full text-left px-2.5 py-2 text-[12.5px] text-ink-2 hover:bg-surface-hover"
                                >
                                  {m.displayName}
                                  {m.jobTitle && <span className="text-[11px] text-ink-3"> · {m.jobTitle}</span>}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        <label className="flex items-center gap-2 text-[12.5px] text-ink-2 h-8">
                          <input
                            type="checkbox"
                            checked={editTopOfOrg}
                            onChange={(e) => {
                              const next = e.target.checked;
                              setEditTopOfOrg(next);
                              if (next) setEditManagerId('');
                            }}
                            className="h-3.5 w-3.5 accent-[var(--accent)]"
                          />
                          Top of org
                        </label>
                        <div className="flex gap-2 items-center h-8">
                          <button
                            onClick={() => saveCorrection(user.id)}
                            disabled={corrSaving}
                            className="h-8 px-3.5 text-[12.5px] font-semibold rounded-ctl bg-accent text-[var(--on-accent)] disabled:opacity-55"
                          >
                            {corrSaving ? 'Saving…' : 'Save'}
                          </button>
                          <button
                            onClick={closeEdit}
                            className="h-8 px-3 text-[12.5px] font-semibold rounded-ctl border border-line-strong bg-surface text-ink-2 hover:bg-surface-hover"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                      <p className="text-[11.5px] text-ink-3 mt-2 leading-[1.5]">
                        A correction overrides Vantagepoint and Azure AD for this person everywhere in the
                        app, including the org chart. It does not change either system of record.
                      </p>
                      {corrError && <p className="text-[12px] text-red-600 dark:text-red-400 mt-1.5">{corrError}</p>}
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {filtered.length === 0 && (
            <p className="text-center py-9 text-[12.5px] text-ink-3">
              {tab === 'corrected'
                ? 'Nobody has a correction yet.'
                : tab === 'admins'
                  ? 'No admins match this search.'
                  : 'Nobody matches this search.'}
            </p>
          )}
        </div>
      )}

      <ConfirmDialog
        open={Boolean(pendingExclude)}
        title={`Exclude ${pendingExclude?.displayName || 'this person'} from the directory?`}
        body="They disappear from the directory, search, the org chart and every department for all 739 people. Their profile data is kept, and you can remove the exclusion from the Excluded tab."
        detail="The exclusion matches on name as well as account id, so a re-synced account for the same person stays excluded."
        confirmLabel="Exclude"
        confirmPhrase={pendingExclude?.displayName || undefined}
        onConfirm={confirmExclude}
        onCancel={() => setPendingExclude(null)}
      />
    </div>
  );
}

/**
 * The audit list. Rules, not people — see removeExclusionRule for why the two
 * lists cannot be joined back into one row per person.
 */
function ExclusionRules({ rules, onRemove, dirty, onRefresh }) {
  const entries = [
    ...rules.names.map((name) => ({ kind: 'name', value: name })),
    ...rules.ids.map((id) => ({ kind: 'id', value: id })),
  ];

  return (
    <div className="space-y-3">
      <div className="flex items-start gap-2.5 rounded-card border border-line bg-surface-2 px-3.5 py-3">
        <span className="text-[13px] leading-none mt-[2px]" aria-hidden>&#9432;</span>
        <p className="text-[12px] text-ink-2 leading-[1.55]">
          <b className="font-bold text-ink">Exclusions match on name as well as account id.</b> That is
          what stops a re-synced account reappearing after a tenant migration — the same person comes
          back from Azure AD with a new object id, and the name rule still catches them. It also means
          someone stays hidden while <em>any</em> rule below still matches them, so removing one rule
          may not be enough.
        </p>
      </div>

      {dirty && (
        <div className="flex items-center gap-3 flex-wrap rounded-card border border-accent-line bg-accent-soft px-3.5 py-2.5">
          <p className="text-[12px] text-ink-2 flex-1 min-w-[200px]">
            Rule removed. Excluded people are filtered out during the directory sync, so they come back
            on the next one.
          </p>
          {onRefresh && (
            <button
              onClick={onRefresh}
              className="h-7 px-2.5 text-[12px] font-semibold rounded-ctl bg-accent text-[var(--on-accent)]"
            >
              Re-sync now
            </button>
          )}
        </div>
      )}

      <div className="rounded-card border border-line bg-surface shadow-e1 overflow-hidden">
        <div className="grid grid-cols-[minmax(0,1fr)_96px_110px] items-center gap-3 h-9 px-3.5 bg-surface-2 border-b border-line
                        text-[10.5px] font-bold uppercase tracking-[0.07em] text-ink-3">
          <div>Rule</div>
          <div>Matches on</div>
          <div className="text-right">Actions</div>
        </div>
        {entries.map((entry) => (
          <div
            key={`${entry.kind}:${entry.value}`}
            className="grid grid-cols-[minmax(0,1fr)_96px_110px] items-center gap-3 min-h-[52px] px-3.5
                       border-b border-line last:border-b-0 hover:bg-surface-hover"
          >
            <div className="min-w-0">
              {entry.kind === 'name' ? (
                <>
                  <span className="block text-[13px] font-semibold text-ink truncate">{titleCase(entry.value)}</span>
                  <span className="block text-[11px] text-ink-3 truncate">
                    Also matches first + last name, so middle names do not defeat the rule
                  </span>
                </>
              ) : (
                <>
                  <span className="block text-[12.5px] font-mono text-ink truncate">{entry.value}</span>
                  <span className="block text-[11px] text-ink-3 truncate">
                    Account id only — no name recorded, so a re-synced account would reappear
                  </span>
                </>
              )}
            </div>
            <div>
              <Pill tone="neutral">{entry.kind === 'name' ? 'Name' : 'Account id'}</Pill>
            </div>
            <div className="text-right">
              <button
                onClick={() => onRemove(entry.kind === 'name' ? { name: entry.value } : { id: entry.value })}
                className="h-7 px-2.5 text-[12px] font-semibold rounded-ctl border border-line-strong bg-surface text-ink-2 hover:bg-surface-hover"
              >
                Remove
              </button>
            </div>
          </div>
        ))}
        {entries.length === 0 && (
          <p className="text-center py-9 text-[12.5px] text-ink-3">
            Nobody is excluded. Excluding someone here hides them from all 739 people.
          </p>
        )}
      </div>
    </div>
  );
}
